<script setup lang="ts">
    import { useModalStore } from '@/stores/modals';
    import { useBookingStore } from '@/stores/booking';
    import { formatAddress } from '@/helpers/formatAddress';
    import { computed } from 'vue';
    import { getNearByGymLocations } from '~/helpers/geoLocation';
    import { ILocation } from '~/helpers/gymClusters';
    import { fetchAndStoreAvailability } from '~/helpers/fetchData';
    import { useAvailabilityStore } from '~/stores/booking-availability';
    import { useErrorStore } from '~/stores/error';
    import { useRuntimeConfig } from 'nuxt/app';
    import { nearByGymLocationSearchRadius, visibleNearByGymsLimit } from '~/helpers/constants';
    import { fetchStandardPackages, resetStandardPackages } from '~/helpers/fetchStandardPackages';
    import { Campaign } from '../enums';

    // props
    const props = defineProps<{
        allGyms: ILocation[];
    }>();    

    const bookingStore = useBookingStore();
    const modalStore = useModalStore();
    const availabilityStore = useAvailabilityStore();
    const errorStore = useErrorStore();
    const runtimeConfig = useRuntimeConfig();

    const locationData = computed(() => ({
        displayName: bookingStore.state.display_name,
        mapsUrl: bookingStore.state.maps_url,
        address: formatAddress(bookingStore.state.address),
    }));

    const nearByLocations = computed(() => bookingStore.state.near_by_location);

    const navigateToSelectedGym = async (selectedGym: ILocation) => {
        availabilityStore.resetAvailability();
        resetStandardPackages();
        const nearbyGyms = getNearByGymLocations(selectedGym, props.allGyms, nearByGymLocationSearchRadius);

        bookingStore.updateBookingState({
            display_name: selectedGym.location_name,
            currency: selectedGym.currency,
            address: selectedGym.address,
            booking_business: selectedGym.booking_business,
            service_id: selectedGym.service_id,
            timezone: selectedGym.timezone,
            timezone_name: selectedGym.timezone_name,
            gym_switched: true,
            is_loader: true,
            country_code: selectedGym?.country_code ?? bookingStore.state.country_code,
            maps_url: selectedGym.maps_url,
            near_by_location: nearbyGyms.slice(0, visibleNearByGymsLimit),
        });

        if (selectedGym.is_pricing_panel_enabled) await fetchStandardPackages(selectedGym.location_id);

        await fetchAndStoreAvailability(
            bookingStore,
            availabilityStore,
            errorStore,
            runtimeConfig,
            selectedGym.location_name,
            true
        );
        bookingStore.updateBookingState({ is_loader: false });
    };
</script>

<template>
    <!-- LOCATION, DATE & TIME BLOCK -->
    <div class="mb-8">
        <!-- heading -->
        <div id="website-booking-form" class="mb-2" v-once>
            <p class="font-semibold text-base">Current location selected</p>
        </div>

        <!-- selected location -->
        <div class="bg-[#F7F6F3] rounded-2xl p-4">
            <div class="flex items-center">
                <Icon icon="up-logo-icon-new" class="up-black-logo"  aria-hidden="true"/>
                <div>
                    <p class="font-medium text-lg mb-[2px]">
                        {{ locationData.displayName }}
                    </p>
                    <div class="flex text-sm">
                        <Icon icon="place" class="location-icon-small" aria-hidden="true"/>
                        <a
                            :href="locationData.mapsUrl"
                            class="underline underline-offset-[3px]"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {{ locationData.address }}
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <!-- GIFT VOUCHER T&C -->
        <div class="border rounded-2xl p-[14px] border-[#D3D4D5] mt-4" v-if="bookingStore.state.utm_campaign === Campaign.giftOfHealth2026">
            <div class="flex">
                <Icon icon="information-red" class="mr-4" aria-hidden="true"/>
                <div>
                    <div class="flex text-sm">
                        <p>Vouchers can only be redeemed at the gym they were issued for, as pricing varies by location. If the gym above doesn't match your voucher, switch below. 
                            <a
                                href="https://ultimateperformance.com/gifting-terms"
                                class="underline underline-offset-[3px] text-[#7C2629]"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Gift of Health terms and conditions
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- near by location  -->
        <div class="my-5 md:mt-4 md:mb-8">
            <p class="font-semibold text-base">
                Switch to another nearby location
            </p>
            <p class="text-[#888B8D] leading-4 text-sm mb-3">
                Tap below to view available consultation times
            </p>

            <ul class="grid grid-cols-1 md:grid-cols-2 gap-2">
                <li
                    v-for="item in nearByLocations"
                    :key="item.location_id"
                    @click="navigateToSelectedGym(item)"
                    class="near-by-location-items"
                >
                    {{ item.location_name }}
                </li>

                <li @click="modalStore.updateModal('locationModal', true)" class="other-location-link">
                    <span>Other UP locations</span>
                    <Icon icon="arrow-up-right" alt="U.P website" class="other-location-icon-small"  aria-hidden="true"/>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
    .up-black-logo {
        width: 40px !important;
        height: 40px !important;
        padding: 8px;
        margin-right: 16px;
        background: #000000;
        border-radius: 50%;
    }

    .location-icon-small {
        width: 16px !important;
        height: 16px !important;
        margin-right: 8px;
        margin-top: 3px;
    }

    .other-location-icon-small {
        width: 16px !important;
        height: 16px !important;
        display: inline;
        margin-left: 4px;
    }

    .near-by-location-items {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid rgb(211 212 213);
        border-radius: 12px;
        padding: 12px 24px;
    }

    .near-by-location-items:hover {
        opacity: .8;
        border: 1px solid rgb(211 212 213 / 80%);
    }

    .other-location-link {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid rgb(124 38 41);
        color: #7C2629;
        border-radius: 12px;
        padding: 12px 24px;
    }

    .other-location-link:hover {
        opacity: .8;
        border: 1px solid rgb(124 38 41 / 80%);
    }
</style>
